import React, { useState, useEffect, useMemo } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import CSTop from '../CS/CSTop';
import InputSearch from '../common/InputSearch';
import HButton from '../common/HButton';
import styled from 'styled-components';
import DevicePagination from '../registration/DevicePagination';
import DropdownList from '../auth/styled/DropdownList';
import { listVersionChecks, listVersionCheckModels, setDeviceTargetVersions } from '../../modules/cs';
import { listVersionChecks as apiListVersionChecks } from '../../lib/api/cs';
import Spinner from '../common/Spinner';

const Content = styled.div`
  position: relative;
  margin: 0 auto;
  width: 1250px;
  min-width: 1250px;
  padding: 64px 40px 40px 40px;

  table.device {
    border-collapse: collapse;
    text-align: center;
    line-height: 1.5;
    width: 100%;
    margin-bottom: 32px;
    color: #212529;
  }

  table.device thead {
    background-color: white;
  }
  table.device thead th {
    min-width: 50px;
  }
  table.device th {
    font-family: 'Source Sans Pro';
    font-weight: 600;
    font-size: 15px;
    color: #59616a;
    vertical-align: middle !important;
  }

  table.device td,
  table.device th {
    padding: 0.75rem;
    border: 1px solid #e7e9ea;
  }

  table.device tbody {
    vertical-align: middle;
  }
  table.device td {
    font-family: 'Source Sans Pro';
    font-size: 15px;
    color: rgba(48, 57, 69, 0.8);
  }

  .checkboxDiv {
    display: flex;
    width: 100%;
    flex-basis: 24px;
    align-items: center;
    justify-content: center;
  }

  .checkbox {
    display: flex;
    width: 100%;
    flex-basis: 24px;
    align-items: center;
    justify-content: center;
  }

  input[type='checkbox'] {
    background-image: url(${process.env.PUBLIC_URL}/images/settings/icon-24-checkbox-active.svg);
    -webkit-appearance: none;
    -moz-appearance: none;
    width: 24px;
    height: 24px;
    margin: 0;
    outline: none;
    cursor: pointer;

    &:checked {
      background-image: url(${process.env.PUBLIC_URL}/images/settings/icon-24-checkbox-selected.svg);
    }
    &:disabled {
      background-image: url(${process.env.PUBLIC_URL}/images/settings/icon-24-checkbox-disabled.svg);
      cursor: not-allowed;
    }
    &:checked:disabled {
      background-image: url(${process.env.PUBLIC_URL}/images/settings/icon-24-checked-disabled.svg);
    }
  }
`;

const TableTop = styled.div`
  display: flex;
  justify-content: space-between;
  align-items: center;
  font-family: 'Source Sans Pro';
  font-weight: 600;
  font-size: 25px;
  color: #59616a;
  margin-bottom: 24px;
`;

const CheckVersionPage = () => {
  const dispatch = useDispatch();
  const { versionChecks, versionCheckModels, loading, error, user, setTargetVersionsResult } = useSelector(({ cs, loading, user }) => ({
    versionChecks: cs.versionChecks,
    versionCheckModels: cs.versionCheckModels,
    loading: loading['cs/LIST_VERSION_CHECKS'] || loading['cs/SET_DEVICE_TARGET_VERSIONS'],
    error: cs.versionChecksError,
    user: user.user,
    setTargetVersionsResult: cs.setTargetVersionsResult,
  }));

  const [search, setSearch] = useState('');
  const [modelFilter, setModelFilter] = useState(null);
  const [osFilter, setOsFilter] = useState(null);
  const [gpuFilter, setGpuFilter] = useState(null);
  const [currentVersionFilter, setCurrentVersionFilter] = useState(null); // NEW
  const [page, setPage] = useState(1);
  const [resetKey, setResetKey] = useState(0);

  const [selectedVersions, setSelectedVersions] = useState({});
  
  const [selectedDevices, setSelectedDevices] = useState([]);
  const [selectAll, setSelectAll] = useState(false);
  
  const [allFilteredUdis, setAllFilteredUdis] = useState([]);
  const [isSelectAllActive, setIsSelectAllActive] = useState(false);
  const [loadingSelectAll, setLoadingSelectAll] = useState(false);

  const isSelectionEnabled = modelFilter && modelFilter !== 'All';

  const checks = versionChecks ? versionChecks.checks ?? [] : [];
  const pageInfo = versionChecks ? versionChecks.pageInfo ?? { count: 0, totalPages: 1 } : { count: 0, totalPages: 1 };

  const backendOptions = versionChecks?.options || {};

  useEffect(() => {
    dispatch(listVersionCheckModels());
  }, [dispatch]);

  const modelOptions = useMemo(() => {
    const fromBackend = backendOptions.models || [];
    const fromModelsEndpoint = versionCheckModels || [];
    const combined = [...new Set([...fromBackend, ...fromModelsEndpoint])];
    return ['All', ...combined];
  }, [backendOptions.models, versionCheckModels]);

  const osOptions = useMemo(
    () => ['All', ...(backendOptions.oses || [])],
    [backendOptions.oses]
  );

  const gpuOptions = useMemo(
    () => ['All', ...(backendOptions.gpus || [])],
    [backendOptions.gpus]
  );

  const currentVersionOptions = useMemo(
    () => ['All', ...(backendOptions.currentVersions || [])],
    [backendOptions.currentVersions]
  );

  useEffect(() => {
    if (modelFilter && !modelOptions.includes(modelFilter)) setModelFilter(null);
  }, [modelOptions, modelFilter]);
  
  useEffect(() => {
    if (osFilter && !osOptions.includes(osFilter)) setOsFilter(null);
  }, [osOptions, osFilter]);
  
  useEffect(() => {
    if (gpuFilter && !gpuOptions.includes(gpuFilter)) setGpuFilter(null);
  }, [gpuOptions, gpuFilter]);

  useEffect(() => {
    if (currentVersionFilter && !currentVersionOptions.includes(currentVersionFilter)) {
      setCurrentVersionFilter(null);
    }
  }, [currentVersionOptions, currentVersionFilter]);

  const normalize = (v) => (!v || v === 'All' ? '' : v);

  const filters = useMemo(() => ({
    searchString: search,
    model: normalize(modelFilter),
    os: normalize(osFilter),
    gpu: normalize(gpuFilter),
    currentVersion: normalize(currentVersionFilter),
  }), [search, modelFilter, osFilter, gpuFilter, currentVersionFilter]);

  useEffect(() => {
    dispatch(listVersionChecks({ filters, page, pageSize: 30 }));
  }, [dispatch, filters, page]);

  useEffect(() => {
    setPage(1);
    setIsSelectAllActive(false);
    setSelectAll(false);
  }, [search, modelFilter, osFilter, gpuFilter, currentVersionFilter]);

  const onSetPage = (newPage) => setPage(newPage);

  const handleReset = () => {
    setSearch('');
    setModelFilter(null);
    setOsFilter(null);
    setGpuFilter(null);
    setCurrentVersionFilter(null);
    setPage(1);
    setResetKey((k) => k + 1);
    setSelectedDevices([]);
    setSelectAll(false);
    setIsSelectAllActive(false);
    setAllFilteredUdis([]);
  };

  const getOptionValue = (opt) =>
    typeof opt === 'string' ? opt : (opt && (opt.value ?? opt.label)) || '';

  const handleVersionChange = (udi, versionData) => {
    setSelectedVersions(prev => ({
      ...prev,
      [udi]: versionData
    }));
  };

  useEffect(() => {
    if (!isSelectionEnabled) {
      setSelectedDevices([]);
      setSelectAll(false);
      setIsSelectAllActive(false);
      setAllFilteredUdis([]);
    }
  }, [isSelectionEnabled]);

  const handleSelectDevice = (checked, udi) => {
    if (!isSelectionEnabled) return;
    
    if (checked) {
      setSelectedDevices(prev => {
        if (!prev.includes(udi)) {
          return [...prev, udi];
        }
        return prev;
      });
    } else {
      setSelectedDevices(prev => prev.filter(u => u !== udi));
      setSelectAll(false);
      setIsSelectAllActive(false);
    }
  };

  const fetchAllFilteredUdis = async () => {
    try {
      console.log('🔍 Fetching with filters:', filters);
      const response = await apiListVersionChecks({ 
        filters, 
        page: 1, 
        pageSize: 10000
      });
      
      const allUdis = response.data?.checks?.map(check => check.udi) || [];
      const allChecks = response.data?.checks || [];
      
      console.log(`✅ Fetched ${allUdis.length} devices`);
      console.log('Sample devices:', allChecks.slice(0, 3).map(c => ({ 
        udi: c.udi, 
        model: c.model, 
        os: c.currentOs, 
        gpu: c.currentGpu 
      })));
      
      return allUdis;
    } catch (error) {
      console.error('❌ Error fetching all filtered UDIs:', error);
      return [];
    }
  };

  const handleSelectAllFiltered = async (checked) => {
    if (!isSelectionEnabled) return;
    
    setSelectAll(checked);
    setIsSelectAllActive(checked);
    
    if (checked) {
      setLoadingSelectAll(true);
      const allUdis = await fetchAllFilteredUdis();
      setAllFilteredUdis(allUdis);
      setSelectedDevices(allUdis);
      setLoadingSelectAll(false);
    } else {
      setSelectedDevices([]);
      setAllFilteredUdis([]);
    }
  };

  useEffect(() => {
    if (checks.length > 0 && !isSelectAllActive) {
      const currentPageUdis = checks.map(check => check.udi);
      const allSelected = currentPageUdis.every(udi => selectedDevices.includes(udi));
      setSelectAll(allSelected && selectedDevices.length > 0);
    } else if (isSelectAllActive && checks.length > 0) {
      setSelectAll(true);
    }
  }, [checks, selectedDevices, isSelectAllActive]);

  const handleBulkAction = async () => {
    if (selectedDevices.length === 0) {
      alert('No devices selected');
      return;
    }

    // Always fetch full data if we have devices not on current page
    const devicesNotOnCurrentPage = selectedDevices.filter(udi => !checks.find(c => c.udi === udi));
    const needsFullData = devicesNotOnCurrentPage.length > 0;
    
    let allChecksData = checks;
    
    if (needsFullData) {
      // Fetch all filtered devices to get their availableVersions
      setLoadingSelectAll(true);
      try {
        const response = await apiListVersionChecks({ 
          filters, 
          page: 1, 
          pageSize: 10000
        });
        allChecksData = response.data?.checks || [];
      } catch (error) {
        console.error('Error fetching all device data:', error);
        alert('Error fetching device data. Please try again.');
        setLoadingSelectAll(false);
        return;
      }
      setLoadingSelectAll(false);
    }

    const devicesData = selectedDevices.map(udi => {
      const check = allChecksData.find(c => c.udi === udi);
      
      if (!check) {
        console.warn(`No data found for UDI: ${udi}`);
        return null;
      }

      const selectedVersion = selectedVersions[udi] || 
        (check.availableVersions && check.availableVersions[0]);

      if (!selectedVersion) {
        console.warn(`No version available for UDI: ${udi}`);
        return null;
      }

      return {
        udi: udi,
        targetVersion: {
          global: selectedVersion.globalVersion,
          scan3d: selectedVersion.scan3dVersion || selectedVersion.scan3d_version,
          patientdb: selectedVersion.patientDbVersion || selectedVersion.patientdb_version,
          os: selectedVersion.os,
          gpu: selectedVersion.gpu,
          link: selectedVersion.link || ''
        }
      };
    }).filter(d => d !== null);

    if (devicesData.length === 0) {
      alert('No valid devices to process');
      return;
    }

    dispatch(setDeviceTargetVersions({ devices: devicesData }));
  };

  useEffect(() => {
    if (setTargetVersionsResult) {
      const { results } = setTargetVersionsResult;
      const message = `
      Target versions set successfully:
      - Created: ${results.created.length}
      - Updated: ${results.updated.length}
      - Errors: ${results.errors.length}
      ${results.errors.length > 0 ? '\n\nErrors:\n' + results.errors.map(e => `${e.udi}: ${e.error}`).join('\n') : ''}
      `.trim();
      
      alert(message);
      
      dispatch(listVersionChecks({ filters, page, pageSize: 30 }));

      handleClearSelection();
    }
  }, [setTargetVersionsResult]);

  const handleClearSelection = () => {
    setSelectedDevices([]);
    setSelectAll(false);
    setIsSelectAllActive(false);
    setAllFilteredUdis([]);
  };

  const [globalVersionSelection, setGlobalVersionSelection] = useState(null);

  const handleGlobalVersionChange = (versionData) => {
    if (!versionData || selectedDevices.length === 0) return;

    const newSelectedVersions = { ...selectedVersions };
    
    selectedDevices.forEach(udi => {
      const check = checks.find(c => c.udi === udi);
      
      if (check && check.availableVersions) {
        const matchingVersion = check.availableVersions.find(
          v => v.globalVersion === versionData.globalVersion
        );
        
        if (matchingVersion) {
          newSelectedVersions[udi] = matchingVersion;
        }
      }
    });

    setSelectedVersions(newSelectedVersions);
    setGlobalVersionSelection(versionData);
  };

  const getFilteredVersions = (availableVersions) => {
    if (!availableVersions) return [];
    
    let filtered = [...availableVersions];
    
    if (osFilter && osFilter !== 'All') {
      filtered = filtered.filter(v => v.os === osFilter);
    }
    
    if (gpuFilter && gpuFilter !== 'All') {
      filtered = filtered.filter(v => v.gpu === gpuFilter);
    }
    
    return filtered;
  };

  const getCommonVersions = () => {
    if (selectedDevices.length === 0) return [];

    const selectedChecks = checks.filter(check => selectedDevices.includes(check.udi));
    
    if (selectedChecks.length === 0) {
      console.log('⚠️ No selected devices found on current page');
      return [];
    }

    if (selectedChecks.length === 1) {
      return getFilteredVersions(selectedChecks[0].availableVersions);
    }

    const firstDeviceVersions = getFilteredVersions(selectedChecks[0].availableVersions);
    
    const common = firstDeviceVersions.filter(version => 
      selectedChecks.every(check => {
        const filtered = getFilteredVersions(check.availableVersions);
        return filtered?.some(v => v.globalVersion === version.globalVersion);
      })
    );

    return common;
  };

  const commonVersions = getCommonVersions();

  if (error) {
    return (
      <>
        <CSTop />
        <Content>
          <div>Error: {error.message}</div>
        </Content>
      </>
    );
  }

  return (
    <>
      <CSTop />
      <Content>
        {(loading || loadingSelectAll) && (
          <div
            style={{
              position: 'absolute',
              inset: 0,
              display: 'grid',
              placeItems: 'center',
              background: 'rgba(255,255,255,0.6)',
              backdropFilter: 'blur(2px)',
              zIndex: 10,
            }}
          >
            <Spinner />
            {loadingSelectAll && (
              <div style={{ marginTop: '10px', fontSize: '14px', color: '#666' }}>
                Selecting all filtered devices...
              </div>
            )}
          </div>
        )}

        {!isSelectionEnabled && (
          <div style={{
            padding: '16px',
            marginBottom: '16px',
            backgroundColor: '#FFF3CD',
            border: '1px solid #FFC107',
            borderRadius: '4px',
            color: '#856404',
            fontSize: '14px',
            fontFamily: 'Source Sans Pro'
          }}>
            ⚠️ Please select a Model filter to enable device selection
          </div>
        )}

        <TableTop>
          <div>Check Version</div>
          {selectedDevices.length > 0 && isSelectionEnabled && (
            <div style={{ display: 'flex', alignItems: 'center', gap: '16px' }}>
              <div style={{ fontSize: '16px', color: '#FF6C00' }}>
                {isSelectAllActive ? (
                  <>All {selectedDevices.length} filtered device(s) selected</>
                ) : (
                  <>{selectedDevices.length} device(s) selected</>
                )}
              </div>
              
              {selectedDevices.length > 1 && (
                <div style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
                  <span style={{ fontSize: '14px', color: '#59616a' }}>
                    Apply version to all:
                  </span>
                  {commonVersions.length > 0 ? (
                    <select
                      value={globalVersionSelection ? JSON.stringify({
                        globalVersion: globalVersionSelection.globalVersion,
                        os: globalVersionSelection.os,
                        gpu: globalVersionSelection.gpu
                      }) : ''}
                      onChange={(e) => {
                        if (!e.target.value) return;
                        const versionData = JSON.parse(e.target.value);
                        const version = commonVersions.find(
                          v => v.globalVersion === versionData.globalVersion &&
                               v.os === versionData.os &&
                               v.gpu === versionData.gpu
                        );
                        if (version) {
                          handleGlobalVersionChange(version);
                        }
                      }}
                      style={{
                        padding: '6px 12px',
                        border: '1px solid #e7e9ea',
                        borderRadius: '4px',
                        backgroundColor: 'white',
                        fontSize: '14px',
                        cursor: 'pointer',
                        minWidth: '120px',
                        fontWeight: 'bold',
                        color: '#FF6C00'
                      }}
                    >
                      <option value="">Select version...</option>
                      {commonVersions.map((version, idx) => {
                        const optionValue = JSON.stringify({
                          globalVersion: version.globalVersion,
                          os: version.os,
                          gpu: version.gpu
                        });
                        
                        const sameVersionCount = commonVersions.filter(
                          v => v.globalVersion === version.globalVersion
                        ).length;
                        
                        const sameVersionIndex = commonVersions
                          .slice(0, idx)
                          .filter(v => v.globalVersion === version.globalVersion)
                          .length;
                        
                        const displayText = sameVersionCount > 1 
                          ? `${version.globalVersion} (${sameVersionIndex + 1})`
                          : version.globalVersion;
                        
                        return (
                          <option key={idx} value={optionValue}>
                            {displayText}
                          </option>
                        );
                      })}
                    </select>
                  ) : (
                    <span style={{
                      padding: '6px 12px',
                      fontSize: '13px',
                      color: '#856404',
                      backgroundColor: '#FFF3CD',
                      border: '1px solid #FFC107',
                      borderRadius: '4px'
                    }}>
                      No common versions on this page
                    </span>
                  )}
                </div>
              )}
            </div>
          )}
        </TableTop>
        
        <div style={{ display: 'flex', alignItems: 'flex-end', gap: '16px', marginBottom: '24px', width: '100%', justifyContent: 'space-between' }}>
          <div style={{ display: 'flex', gap: '16px' }}>
            <InputSearch
              key={`search-${resetKey}`}
              size="Medium"
              placeholder={'Search'}
              width="355px"
              value={search}
              onSearchEnterPress={setSearch}
              onChange={(e) => setSearch(e.target.value)}
            />
            
            <DropdownList
              key={`model-${resetKey}`}
              width="180px"
              height="40px"
              items={modelOptions}
              value={modelFilter ?? undefined}
              defaultValue="Model"
              onSelect={(opt) => {
                const v = getOptionValue(opt);
                setModelFilter(v === 'All' ? null : v);
              }}
              zIndex={1}
            />
            
            <DropdownList
              key={`os-${resetKey}`}
              width="180px"
              height="40px"
              items={osOptions}
              value={osFilter ?? undefined}
              defaultValue="OS"
              onSelect={(opt) => {
                const v = getOptionValue(opt);
                setOsFilter(v === 'All' ? null : v);
              }}
              zIndex={1}
            />
            
            <DropdownList
              key={`gpu-${resetKey}`}
              width="180px"
              height="40px"
              items={gpuOptions}
              value={gpuFilter ?? undefined}
              defaultValue="GPU"
              onSelect={(opt) => {
                const v = getOptionValue(opt);
                setGpuFilter(v === 'All' ? null : v);
              }}
              zIndex={1}
            />

            {/* NEW: Current Version Filter */}
            <DropdownList
              key={`currentVersion-${resetKey}`}
              width="180px"
              height="40px"
              items={currentVersionOptions}
              value={currentVersionFilter ?? undefined}
              defaultValue="Current Version"
              onSelect={(opt) => {
                const v = getOptionValue(opt);
                setCurrentVersionFilter(v === 'All' ? null : v);
              }}
              zIndex={1}
            />
          </div>
          
          <div style={{ display: 'flex', gap: '16px' }}>
            {selectedDevices.length > 0 && isSelectionEnabled && (
              <>
                <HButton
                  category="Secondary"
                  size="Medium"
                  text={'CLEAR SELECTION'}
                  onClick={handleClearSelection}
                />
                <HButton
                  category="Primary"
                  size="Medium"
                  text={'PROCESS SELECTED'}
                  onClick={handleBulkAction}
                />
                <HButton
                  category="Tertiary"
                  size="Medium"
                  text={'RESET'}
                  onClick={handleReset}
                />
              </>
            )}
          </div>
        </div>
        
        <div
          style={{
            float: 'right',
            height: '20px',
            display: 'flex',
            alignItems: 'center',
            fontFamily: 'Source Sans Pro',
            fontSize: '15px',
            color: '#303945cc',
            marginBottom: '15px',
          }}
        >
          Total Scanners: {pageInfo.count}
          {isSelectAllActive && (
            <span style={{ marginLeft: '10px', color: '#FF6C00' }}>
              (All filtered selected)
            </span>
          )}
        </div>
        
        <table className="device" style={{ marginTop: '24px', width: '100%' }}>
          <thead>
            <tr>
              <th rowSpan={2} style={{ padding: 0, width: '50px' }}>
                <div 
                  className="checkboxDiv" 
                  title={
                    !isSelectionEnabled 
                      ? "Select a model filter first" 
                      : (isSelectAllActive ? "Deselect all filtered" : "Select all filtered")
                  }
                >
                  <input
                    className="checkbox"
                    type="checkbox"
                    checked={selectAll}
                    onChange={(e) => handleSelectAllFiltered(e.target.checked)}
                    disabled={loadingSelectAll || !isSelectionEnabled}
                  />
                </div>
              </th>
              <th rowSpan={2}>UDI</th>
              <th rowSpan={2}>Model</th>
              <th rowSpan={2}>Country</th>
              <th rowSpan={2}>Region</th>
              <th colSpan={2}>Scans</th>
              <th colSpan={3}>Target</th>
              <th colSpan={3}>Current</th>
            </tr>
            <tr>
              <th>Number</th>
              <th>Last Date</th>
              <th>Version</th>
              <th>OS</th>
              <th>GPU</th>
              <th>Version</th>
              <th>OS</th>
              <th>GPU</th>
            </tr>
          </thead>
          <tbody>
            {checks.length > 0 ? (
              checks.map((check) => {
                const filteredVersions = getFilteredVersions(check.availableVersions);
                
                const targetFromDb = filteredVersions?.find(
                  v => v.globalVersion === check.targetVersion
                );

                const selected = selectedVersions[check.udi] || 
                  targetFromDb ||
                  (filteredVersions && filteredVersions[0]) || 
                  { globalVersion: check.targetVersion, os: check.targetOs, gpu: check.targetGpu };

                const isChecked = selectedDevices.includes(check.udi);

                return (
                  <tr key={check.udi}>
                    <td style={{ padding: 0 }}>
                      <div className="checkboxDiv">
                        <input
                          className="checkbox"
                          type="checkbox"
                          checked={isChecked}
                          onChange={(e) => handleSelectDevice(e.target.checked, check.udi)}
                          disabled={!isSelectionEnabled}
                          title={!isSelectionEnabled ? "Select a model filter first" : ""}
                        />
                      </div>
                    </td>
                    <td>{check.udi}</td>
                    <td>{check.model}</td>
                    <td>{check.country}</td>
                    <td>{check.region}</td>
                    <td>{check.totalScans || 0}</td>
                    <td>
                      {check.lastScanDate
                        ? new Date(check.lastScanDate).toLocaleDateString('fr-FR')
                        : 'N/A'}
                    </td>
                    <td>
                      {filteredVersions && filteredVersions.length > 0 ? (
                        <select
                          value={JSON.stringify({
                            globalVersion: selected.globalVersion,
                            os: selected.os,
                            gpu: selected.gpu
                          })}
                          onChange={(e) => {
                            const versionData = JSON.parse(e.target.value);
                            const version = filteredVersions.find(
                              v => v.globalVersion === versionData.globalVersion && 
                                   v.os === versionData.os && 
                                   v.gpu === versionData.gpu
                            );
                            if (version) {
                              handleVersionChange(check.udi, version);
                            }
                          }}
                          style={{
                            padding: '4px 8px',
                            border: '1px solid #e7e9ea',
                            borderRadius: '4px',
                            backgroundColor: 'white',
                            fontSize: '14px',
                            cursor: 'pointer',
                            minWidth: '100px'
                          }}
                        >
                          {filteredVersions.map((version, idx) => {
                            const optionValue = JSON.stringify({
                              globalVersion: version.globalVersion,
                              os: version.os,
                              gpu: version.gpu
                            });
                            
                            const sameVersionCount = filteredVersions.filter(
                              v => v.globalVersion === version.globalVersion
                            ).length;
                            
                            const sameVersionIndex = filteredVersions
                              .slice(0, idx)
                              .filter(v => v.globalVersion === version.globalVersion)
                              .length;
                            
                            const displayText = sameVersionCount > 1 
                              ? `${version.globalVersion} (${sameVersionIndex + 1})`
                              : version.globalVersion;
                            
                            return (
                              <option key={idx} value={optionValue}>
                                {displayText}
                              </option>
                            );
                          })}
                        </select>
                      ) : (
                        'N/A'
                      )}
                    </td>
                    <td>{selected.os || 'N/A'}</td>
                    <td>{selected.gpu || 'N/A'}</td>
                    <td>{check.currentVersion || 'N/A'}</td>
                    <td>{check.currentOs || 'N/A'}</td>
                    <td>{check.currentGpu || 'N/A'}</td>
                  </tr>
                );
              })
            ) : (
              <tr>
                <td colSpan="13" style={{ textAlign: 'center', padding: '20px' }}>
                  No devices found
                </td>
              </tr>
            )}
          </tbody>
        </table>
        
        <DevicePagination pageInfo={pageInfo} paginate={onSetPage} />
      </Content>
    </>
  );
};

export default CheckVersionPage;